/**
 * A tela de preferências de aviso — parte D1 (01/10/2026), peça D do desenho. Substitui a
 * `PreferenceMatrix` em `/notifications/preferences` (mesma rota e permissão).
 *
 * D3 (passo 19): a `variante` troca a API e o cache, a cara das categorias, os textos, o cartão de push, a moldura e a disposição das
 * linhas — o portal usa esta mesma tela em `/portal/preferencias`. Sem `variante` vale a do interno.
 */
import { useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { usePreferenciasDeAviso, useRestaurarPadroesDeAviso } from "@/hooks/usePreferenciasDeAviso";
import { CartaoPushDoAparelho } from "./CartaoPushDoAparelho";
import { CartaoSomDoAparelho } from "./CartaoSomDoAparelho";
import { LinhaDeAviso } from "./LinhaDeAviso";
import { GRADES, type DisposicaoDaLinha } from "./LinhaDeAviso.constants";
import { VARIANTE_INTERNA, type VarianteDasPreferencias } from "./PreferenciasDeAviso.variante";

/** O esqueleto ocupa o mesmo lugar que a lista real (as categorias do servidor) e usa a MESMA grade da linha. */
function Esqueleto({ linhas, disposicao }: { linhas: number; disposicao: DisposicaoDaLinha }) {
  const grade = GRADES[disposicao];
  return (
    <div
      aria-busy="true"
      aria-label="Carregando preferências"
      data-testid="preferencias-carregando"
    >
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className={grade.linha}>
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-64" />
            </div>
          </div>
          <div className={grade.pilulas}>
            {Array.from({ length: 4 }, (_, j) => (
              <Skeleton key={j} className="h-9 w-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PreferenciasDeAviso({
  variante = VARIANTE_INTERNA,
}: { variante?: VarianteDasPreferencias } = {}) {
  const { data, isLoading, isError, refetch, isFetching } = usePreferenciasDeAviso(
    variante.adaptador
  );
  const restaurar = useRestaurarPadroesDeAviso(variante.adaptador);
  const [confirmando, setConfirmando] = useState(false);
  // O diálogo fecha ao confirmar: sem o `onError`, a falha não chegaria a ninguém.
  const restaurarPadroes = () =>
    restaurar.mutate(undefined, {
      onError: () =>
        toast({
          variant: "destructive",
          title: "Não foi possível restaurar os padrões. Tente de novo.",
        }),
    });
  const algumaDiferente = data?.categorias.some((c) => c.nivel !== c.padrao) ?? false;

  return (
    <div className={variante.classeDaRaiz}>
      {!variante.semCabecalho && (
        <div>
          <h1 className="text-2xl font-bold">Preferências de aviso</h1>
          <p className="text-sm text-muted-foreground">
            Escolha, para cada tipo de assunto, como quer ser avisado.
          </p>
        </div>
      )}

      <CartaoSomDoAparelho className={variante.classeDoCartao} />

      <CartaoPushDoAparelho
        basePath={variante.push.basePath}
        textos={variante.push.textos}
        className={variante.classeDoCartao}
      />

      {isError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Não conseguimos carregar suas preferências</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{variante.orientacaoDoErro}</p>
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px] sm:min-h-0"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Tentar de novo
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <Card className={variante.classeDoCartao}>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <CardTitle className="text-lg">Como você quer ser avisado</CardTitle>
            {algumaDiferente && (
              <Button
                variant="ghost"
                size="sm"
                className="min-h-[44px] sm:min-h-0"
                disabled={restaurar.isPending}
                onClick={() => setConfirmando(true)}
              >
                Restaurar padrões
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <p className="border-b pb-3 text-sm text-muted-foreground">{variante.textoDosNiveis}</p>
            {isLoading || !data ? (
              <Esqueleto linhas={variante.linhasDoEsqueleto} disposicao={variante.disposicao} />
            ) : (
              data.categorias.map((c) => (
                <LinhaDeAviso
                  key={c.chave}
                  categoria={c}
                  adaptador={variante.adaptador}
                  categorias={variante.categorias}
                  disposicao={variante.disposicao}
                />
              ))
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent aria-describedby={undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>Voltar todas as categorias ao padrão?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px] sm:min-h-0">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="min-h-[44px] sm:min-h-0" onClick={restaurarPadroes}>
              Restaurar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
